import {
  BenefitsIncomeResponseT,
  BenefitsWillExpireValueT,
  DayOfWeekCapitalizedT,
  EmployedIncomeResponseT,
  IncomeDataT,
  IncomeResponseT,
  IncomeSourceT,
  IncomeSourceValueT,
  MonthlyPaymentDayValueT,
  PaydateScheduleTypeT,
  PaymentDaysValueT,
  PaymentDayValueT,
  PaymentScheduleValueT,
  PaymentWeekValueT,
} from '../../types';
import { isDefined, isNumeric } from '../../utils';

// map account income source values to form income source values.
const SOURCE_FROM_SOURCE_OF_INCOME = {
  'alimony/child_support': 'alimony/child_support',
  employment: 'employed',
  other: 'other',
  retired_benefits: 'retired_benefits',
  self_employment: 'self_employed',
  'social_security/disability_income': 'social_security/disability_income',
  unemployment_benefits: 'unemployment_benefits',
} as const satisfies Record<IncomeSourceT, IncomeSourceValueT>;

/* eslint-disable sort-keys */
const PAYMENT_DAY_MAP = {
  Monday: 'monday',
  Tuesday: 'tuesday',
  Wednesday: 'wednesday',
  Thursday: 'thursday',
  Friday: 'friday',
} as const satisfies Record<DayOfWeekCapitalizedT, PaymentDayValueT>;

const PAYMENT_SCHEDULE_FROM_TYPE = {
  specific_date: 'same_day_each_month',
  relative_date: 'same_day_particular_week',
} as const satisfies Record<PaydateScheduleTypeT, PaymentScheduleValueT>;

const PAYMENT_DAYS_FROM_ORDINAL = {
  '1;15': '1_and_15',
  '1;16': '1_and_16',
  '5;20': '5_and_20',
  '7;22': '7_and_22',
  '10;25': '10_and_25',
  '15;-1': '15_and_last',
} as const satisfies Record<string, PaymentDaysValueT>;

/* eslint-disable quote-props */
const PAYMENT_WEEK_FROM_ORDINAL = {
  '-1': 'last',
  '1': 'first',
  '2': 'second',
  '3': 'third',
  '4': 'fourth',
} as const satisfies Record<string, PaymentWeekValueT>;
/* eslint-enable quote-props */

const BENEFITS_INCOME_SOURCES = [
  'alimony/child_support',
  'other',
  'retired_benefits',
  'social_security/disability_income',
  'unemployment_benefits',
] as const satisfies readonly IncomeSourceT[];

const EMPLOYED_INCOME_SOURCES = ['employment', 'self_employment'] as const satisfies readonly IncomeSourceT[];

/* eslint-enable sort-keys */

type PaymentDaysOrdinalT = keyof typeof PAYMENT_DAYS_FROM_ORDINAL;
type PaymentWeekOrdinalT = keyof typeof PAYMENT_WEEK_FROM_ORDINAL;

// type guard for payment days ordinals
export const isPaymentDaysOrdinal = (value: string): value is PaymentDaysOrdinalT => {
  return value in PAYMENT_DAYS_FROM_ORDINAL;
};

// type guard for payment week ordinals
export const isPaymentWeekOrdinal = (value: string): value is PaymentWeekOrdinalT => {
  return value in PAYMENT_WEEK_FROM_ORDINAL;
};

// type guard for paydate schedule types
export const isPaydateScheduleType = (value: string): value is PaydateScheduleTypeT => {
  return value in PAYMENT_SCHEDULE_FROM_TYPE;
};

// type guard for requests representing 'employed' incomes
export const isEmployedRequest = (incomeRequest: IncomeResponseT): incomeRequest is EmployedIncomeResponseT => {
  return (
    EMPLOYED_INCOME_SOURCES.some((source) => source === incomeRequest.source_of_income)
    || isDefined(incomeRequest.employer_name)
    || isDefined(incomeRequest.work_phone)
    || isDefined(incomeRequest.position)
  );
};

// type guard for requests representing 'benefits' incomes
export const isBenefitsRequest = (incomeRequest: IncomeResponseT): incomeRequest is BenefitsIncomeResponseT => {
  return (
    BENEFITS_INCOME_SOURCES.some((source) => source === incomeRequest.source_of_income)
    || isDefined(incomeRequest.benefits_expiry)
    || isDefined(incomeRequest.benefits_dont_expire)
  );
};

// deserialize net pay per check
export const deserializeNetPayPerCheck = (incomeRequest: IncomeResponseT): number | undefined => {
  if (incomeRequest.net_pay_per_check && isNumeric(incomeRequest.net_pay_per_check)) {
    return parseFloat(incomeRequest.net_pay_per_check);
  }

  return undefined;
};

// deserialize employment income data
export const deserializeEmployment = (incomeRequest: IncomeResponseT): IncomeDataT['employment'] | undefined => {
  return isEmployedRequest(incomeRequest)
    ? {
      employer: incomeRequest.employer_name ?? '',
      phoneNumber: incomeRequest.work_phone ?? '',
      title: incomeRequest.position ?? '',
    }
    : undefined;
};

// deserialize benefits expiration
export const deserializeBenefitsExpiration = (incomeRequest: IncomeResponseT): string | undefined => {
  if (!incomeRequest.benefits_expiry) {
    return undefined;
  }

  const [year, month] = incomeRequest.benefits_expiry.split('-');

  return `${year}-${month}`;
};

// deserialize benefits will expire
export const deserializeBenefitsWillExpire = (incomeRequest: IncomeResponseT): BenefitsWillExpireValueT | undefined => {
  if (incomeRequest.benefits_dont_expire === undefined) {
    return undefined;
  }

  return incomeRequest.benefits_dont_expire ? 'no' : 'yes';
};

// deserialize benefits income
export const deserializeBenefits = (incomeRequest: IncomeResponseT): IncomeDataT['benefits'] | undefined => {
  return isBenefitsRequest(incomeRequest)
    ? {
      expiration: deserializeBenefitsExpiration(incomeRequest),
      willExpire: deserializeBenefitsWillExpire(incomeRequest),
    }
    : undefined;
};

export const deserializeMonthlyPaymentDay = (incomeRequest: IncomeResponseT): MonthlyPaymentDayValueT | undefined => {
  if (
    incomeRequest.paydate_frequency === 'monthly'
    && incomeRequest.paydate_schedule_type === 'specific_date'
    && incomeRequest.paydate_ordinal
  ) {
    if (incomeRequest.paydate_ordinal === '-1') {
      return 'last_day';
    }

    return incomeRequest.paydate_ordinal as MonthlyPaymentDayValueT;
  }

  return undefined;
};

export const deserializePaymentDays = (incomeRequest: IncomeResponseT): PaymentDaysValueT | undefined => {
  if (
    incomeRequest.paydate_frequency === 'twice_monthly'
    && incomeRequest.paydate_ordinal
    && isPaymentDaysOrdinal(incomeRequest.paydate_ordinal)
  ) {
    return PAYMENT_DAYS_FROM_ORDINAL[incomeRequest.paydate_ordinal];
  }

  return undefined;
};

export const deserializePaymentWeek = (incomeRequest: IncomeResponseT): PaymentWeekValueT | undefined => {
  if (
    incomeRequest.paydate_frequency === 'monthly'
    && incomeRequest.paydate_schedule_type === 'relative_date'
    && incomeRequest.paydate_ordinal
    && isPaymentWeekOrdinal(incomeRequest.paydate_ordinal)
  ) {
    return PAYMENT_WEEK_FROM_ORDINAL[incomeRequest.paydate_ordinal];
  }

  return undefined;
};

export const deserializePaymentSchedule = (incomeRequest: IncomeResponseT): PaymentScheduleValueT | undefined => {
  if (
    incomeRequest.paydate_frequency === 'monthly'
    && incomeRequest.paydate_schedule_type
    && isPaydateScheduleType(incomeRequest.paydate_schedule_type)
  ) {
    return PAYMENT_SCHEDULE_FROM_TYPE[incomeRequest.paydate_schedule_type];
  }

  return undefined;
};

export const deserializeIncome = (incomeRequest: IncomeResponseT): IncomeDataT => {
  return {
    amount: deserializeNetPayPerCheck(incomeRequest),
    benefits: deserializeBenefits(incomeRequest),
    day: incomeRequest.paydate_day_of_week && PAYMENT_DAY_MAP[incomeRequest.paydate_day_of_week],
    employment: deserializeEmployment(incomeRequest),
    frequency: incomeRequest.paydate_frequency,
    method: incomeRequest.payment_method,
    monthlyPaymentDay: deserializeMonthlyPaymentDay(incomeRequest),
    nextPayday: incomeRequest.paydate_next_paydate,
    paymentDays: deserializePaymentDays(incomeRequest),
    paymentSchedule: deserializePaymentSchedule(incomeRequest),
    paymentWeek: deserializePaymentWeek(incomeRequest),
    source: incomeRequest.source_of_income && SOURCE_FROM_SOURCE_OF_INCOME[incomeRequest.source_of_income],
  };
};
